import { lazy, Suspense } from 'react';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { getCatalog, getMe, type Me } from '@vinyl/shared';
import { api, ApiError } from './lib/api.ts';
import { meQueryKey } from './auth/session.ts';
import { AppShell } from './shell/AppShell.tsx';
import { SignInPage } from './pages/SignInPage.tsx';
import { AccessDeniedPage } from './pages/AccessDeniedPage.tsx';
import { validateCollectionSearch } from './records/search.ts';
import { useLanguageFromSettings } from './shell/useLanguageFromSettings.ts';
import { keys, useCatalog } from './records/queries.ts';

const SetupStep = lazy(() => import('./discogs/SetupStep.tsx'));

type Context = { queryClient: QueryClient };

async function loadMe(queryClient: QueryClient): Promise<Me | null> {
  return queryClient.ensureQueryData({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await api(getMe);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
}

const rootRoute = createRootRouteWithContext<Context>()({ component: Outlet });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>) => (typeof s.error === 'string' ? { error: s.error } : {}),
  component: SignInPage,
});

const accessDeniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/access-denied',
  validateSearch: (s: Record<string, unknown>) => (typeof s.reason === 'string' ? { reason: s.reason } : {}),
  component: AccessDeniedPage,
});

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/set-password',
  component: lazyRouteComponent(() => import('./pages/SetPasswordPage.tsx'), 'SetPasswordPage'),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context }) => {
    // The catalog settings load alongside the User, so the setup question
    // shows from the first paint instead of replacing a page already shown.
    const [me] = await Promise.all([
      loadMe(context.queryClient),
      context.queryClient.ensureQueryData({ queryKey: keys.catalog, queryFn: () => api(getCatalog) }).catch(() => null),
    ]);
    if (!me) throw redirect({ to: '/sign-in' });
    return { me };
  },
  component: function AppLayout() {
    const { me } = appRoute.useRouteContext();
    useLanguageFromSettings();
    const catalog = useCatalog();
    // The setup question comes first, once, on any page (user-settings spec).
    const setup = catalog.data && !catalog.data.setupDone;
    return (
      <AppShell me={me}>
        {setup ? (
          <Suspense fallback={null}>
            <SetupStep catalog={catalog.data!} />
          </Suspense>
        ) : (
          <Outlet />
        )}
      </AppShell>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/collection' });
  },
});

const collectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/collection',
  validateSearch: validateCollectionSearch,
  component: lazyRouteComponent(() => import('./pages/CollectionPage.tsx')),
});

const str = (v: unknown) => (typeof v === 'string' && v.length <= 300 ? v : undefined);

const addRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/add',
  validateSearch: (s: Record<string, unknown>) => ({ c: str(s.c) }),
  component: lazyRouteComponent(() => import('./pages/AddPage.tsx'), 'AddPage'),
});

const addCopyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/add/copy',
  validateSearch: (s: Record<string, unknown>) => ({ release: str(s.release), c: str(s.c), title: str(s.title), artists: str(s.artists) }),
  component: lazyRouteComponent(() => import('./pages/AddPage.tsx'), 'AddCopyPage'),
});

const editCopyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/copies/$id/edit',
  component: lazyRouteComponent(() => import('./pages/AddPage.tsx'), 'EditCopyPage'),
});

const editReleaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/releases/$id/edit',
  validateSearch: (s: Record<string, unknown>) => ({ copy: str(s.copy) }),
  component: lazyRouteComponent(() => import('./pages/AddPage.tsx'), 'EditReleasePage'),
});

const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/import',
  validateSearch: (s: Record<string, unknown>) => ({ c: str(s.c) }),
  component: lazyRouteComponent(() => import('./pages/ImportPage.tsx')),
});

const appearanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/appearance',
  component: lazyRouteComponent(() => import('./pages/AppearancePage.tsx')),
});

const householdsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/households',
  component: lazyRouteComponent(() => import('./pages/HouseholdsPage.tsx')),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/SettingsPage.tsx')),
});

const aboutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/about',
  component: lazyRouteComponent(() => import('./pages/AboutPage.tsx')),
});

const adminOnly = ({ context }: { context: { me: Me } }) => {
  if (!context.me.isAdmin) throw redirect({ to: '/settings' });
};
const adminPage = <P extends string>(path: P, name: string) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: adminOnly,
    component: lazyRouteComponent(() => import('./pages/AdminPages.tsx'), name as 'AdminIndexPage'),
  });
const adminRoutes = [
  adminPage('/settings/admin', 'AdminIndexPage'),
  adminPage('/settings/admin/users', 'AdminUsersPage'),
  adminPage('/settings/admin/status', 'AdminStatusPage'),
  adminPage('/settings/admin/defaults', 'AdminDefaultsPage'),
  adminPage('/settings/admin/appearance', 'AdminAppearancePage'),
  adminPage('/settings/admin/activity', 'AdminActivityPage'),
];

const licensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/about/licenses',
  component: lazyRouteComponent(() => import('./pages/LicensesPage.tsx')),
});

export const routeTree = rootRoute.addChildren([
  signInRoute,
  accessDeniedRoute,
  setPasswordRoute,
  appRoute.addChildren([indexRoute, collectionRoute, addRoute, addCopyRoute, editCopyRoute, editReleaseRoute, importRoute, settingsRoute, householdsRoute, appearanceRoute, aboutRoute, licensesRoute, ...adminRoutes]),
]);

/** Help pages, by route, for the help completeness test. */
export const HELP_PAGES = {
  '/sign-in': 'signIn',
  '/access-denied': 'accessDenied',
  '/collection': 'collection',
  '/add': 'manual',
  '/import': 'import',
  '/settings': 'settings',
  '/settings/households': 'households',
  '/settings/appearance': 'appearance',
  '/settings/about': 'about',
  '/settings/about/licenses': 'licenses',
  '/set-password': 'setPassword',
} as const;

/** Admin pages; the help completeness test renders them as an admin in both sign-in modes. */
export const ADMIN_HELP_PAGES = {
  '/settings/admin': 'admin',
  '/settings/admin/users': 'adminUsers',
  '/settings/admin/status': 'adminStatus',
  '/settings/admin/defaults': 'adminDefaults',
  '/settings/admin/appearance': 'adminAppearance',
  '/settings/admin/activity': 'adminActivity',
} as const;

export function createAppRouter(queryClient: QueryClient, history?: Parameters<typeof createRouter>[0]['history']) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', ...(history ? { history } : {}) });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
